import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import Icon from '../Icon/Icon';
import AnimatedCard from '../AnimatedCard/AnimatedCard';

// EmailJS Configuration
const EMAILJS_CONFIG = {
  PUBLIC_KEY: '4i8G7EykATzzz6Tzb',
  SERVICE_ID: 'service_4h3nirm',
  CONFIRMATION_TEMPLATE_ID: 'template_deletion', // Reuse same template for confirmation
};

const ConfirmSection = styled.div`
  min-height: 100vh;
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing.xxl} ${theme.spacing.md}`};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.background} 0%, ${({ theme }) => `${theme.colors.primary}11`} 100%);
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => `${theme.spacing.xl} ${theme.spacing.md}`};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => `${theme.spacing.lg} ${theme.spacing.sm}`};
  }
`;

const Container = styled.div`
  max-width: 700px;
  width: 100%;
  position: relative;
  z-index: 10;
`;

const IconWrapper = styled.div<{ status: 'loading' | 'success' | 'error' }>`
  display: flex;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  svg {
    color: ${({ status, theme }) => {
      if (status === 'success') return '#28a745';
      if (status === 'error') return '#dc3545';
      return theme.colors.primary;
    }};
    
    ${({ status }) => status === 'loading' && `
      animation: spin 1s linear infinite;
    `}
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const Title = styled.h2`
  text-align: center;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: clamp(1.8rem, 5vw, 2.5rem);
`;

const Message = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  font-size: 1.1rem;
  line-height: 1.8;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

const InfoBox = styled.div<{ type: 'info' | 'success' | 'error' }>`
  background-color: ${({ type }) => {
    if (type === 'success') return '#d4edda';
    if (type === 'error') return '#f8d7da';
    return '#d1ecf1';
  }};
  border: 2px solid ${({ type }) => {
    if (type === 'success') return '#c3e6cb';
    if (type === 'error') return '#f5c6cb';
    return '#bee5eb';
  }};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  color: ${({ type }) => {
    if (type === 'success') return '#155724';
    if (type === 'error') return '#721c24';
    return '#0c5460';
  }};

  h3 {
    margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
    font-size: 1.2rem;
  }

  p {
    margin: 0;
    line-height: 1.6;
  }

  ul {
    margin: ${({ theme }) => theme.spacing.sm} 0 0 ${({ theme }) => theme.spacing.md};
    line-height: 1.8;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const Button = styled(motion.button)<{ variant?: 'primary' | 'danger' }>`
  padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.xl}`};
  background-color: ${({ variant, theme }) => 
    variant === 'danger' ? '#dc3545' : theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.large};
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  min-width: 180px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.large};
    background-color: ${({ variant }) => 
      variant === 'danger' ? '#c82333' : '#0056b3'};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ConfirmDeletion: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'ready' | 'confirming' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [requestId, setRequestId] = useState('');
  const [userEmail, setUserEmail] = useState('');

  // Initialize EmailJS
  useEffect(() => {
    emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);
  }, []);

  // Parse URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get('id');
    const email = urlParams.get('email');

    if (!id || !email) {
      setStatus('error');
      setMessage('Invalid confirmation link. Please check your email and try again.');
      return;
    }

    setRequestId(id);
    setUserEmail(decodeURIComponent(email));
    setStatus('ready');
  }, []);

  const handleConfirm = async () => {
    setStatus('confirming');
    
    try {
      // Send confirmation email to support@radyofone.com using same template
      const result = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.CONFIRMATION_TEMPLATE_ID,
        {
          request_id: requestId,
          from_name: 'Account Deletion System',
          from_email: userEmail,
          deletion_reason: 'CONFIRMED - User clicked confirmation button',
          additional_info: `This is a CONFIRMED deletion request. The user has verified their intent to delete the account.`,
          confirmation_url: window.location.href,
          request_date: new Date().toLocaleString(),
          to_email: 'support@radyofone.com',
          is_confirmation: 'true'
        },
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      console.log('Confirmation email sent successfully:', result);
      
      setStatus('success');
      setMessage('Your account deletion has been confirmed. Our support team will process your request within 24-48 hours. You will receive a final confirmation email once the deletion is complete.');
    } catch (error) {
      console.error('Confirmation email failed:', error);
      setStatus('error');
      setMessage('Failed to send confirmation. Please contact support@radyofone.com directly with your request ID: ' + requestId);
    }
  };

  const handleCancel = () => {
    window.location.href = '/';
  };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <>
            <IconWrapper status="loading">
              <Icon icon={FaSpinner} size={64} />
            </IconWrapper>
            <Title>Loading...</Title>
            <Message>Please wait while we verify your request.</Message>
          </>
        );

      case 'ready':
        return (
          <>
            <Title>Confirm Account Deletion</Title>
            <Message>
              You are about to permanently delete your Radyofone account associated with:<br />
              <strong>{userEmail}</strong>
            </Message>
            <InfoBox type="info">
              <h3>What happens next?</h3>
              <ul>
                <li>Your account and all associated data will be permanently deleted</li>
                <li>This action cannot be undone</li>
                <li>You will lose access to all premium features and saved content</li>
                <li>Processing may take 24-48 hours</li>
              </ul>
            </InfoBox>
            <ButtonGroup>
              <Button
                variant="primary"
                onClick={handleCancel}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Cancel Request
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirm}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Confirm Deletion
              </Button>
            </ButtonGroup>
          </>
        );

      case 'confirming':
        return (
          <>
            <IconWrapper status="loading">
              <Icon icon={FaSpinner} size={64} />
            </IconWrapper>
            <Title>Processing...</Title>
            <Message>Please wait while we process your confirmation.</Message>
          </>
        );

      case 'success':
        return (
          <>
            <IconWrapper status="success">
              <Icon icon={FaCheckCircle} size={64} />
            </IconWrapper>
            <Title>Deletion Confirmed</Title>
            <Message>{message}</Message>
            <InfoBox type="success">
              <h3>Request ID: {requestId}</h3>
              <p>Please save this ID for your records. You can use it to track your deletion request if needed.</p>
            </InfoBox>
            <ButtonGroup>
              <Button
                variant="primary"
                onClick={handleCancel}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Return to Homepage
              </Button>
            </ButtonGroup>
          </>
        );

      case 'error':
        return (
          <>
            <IconWrapper status="error">
              <Icon icon={FaTimesCircle} size={64} />
            </IconWrapper>
            <Title>Error</Title>
            <Message>{message}</Message>
            <InfoBox type="error">
              <h3>Need Help?</h3>
              <p>
                If you continue to experience issues, please contact our support team directly at{' '}
                <strong>support@radyofone.com</strong>
                {requestId && ` and include your Request ID: ${requestId}`}
              </p>
            </InfoBox>
            <ButtonGroup>
              <Button
                variant="primary"
                onClick={handleCancel}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Return to Homepage
              </Button>
            </ButtonGroup>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <ConfirmSection>
      <Container>
        <AnimatedCard delay={0}>
          {renderContent()}
        </AnimatedCard>
      </Container>
    </ConfirmSection>
  );
};

export default ConfirmDeletion;

